/**
 * @fileoverview Creates a Portal thread when KorvoChat session has no threadId yet.
 * @purpose Extracted from handleSendMessage to reduce component cyclomatic complexity (CRAP).
 */

import { ERR_BAD_REQUEST_400 } from '@korvo/core/lib/error-codes';
import { KorvoError } from '@korvo/core/lib/korvo-error';
import { DEFAULT_PORTAL_MODEL } from '@korvo/core/shared/constants/default-models';
import type ThreadsClient from '@korvo/portal-sdk/threads-client';

import type { ChatSession, KorvoChatConfig } from './types';

export async function ensureKorvoChatThreadId(
  activeSession: ChatSession,
  threadsClient: Pick<ThreadsClient, 'createThread'>,
  config: KorvoChatConfig,
  persistSession: (session: ChatSession) => void,
): Promise<{ threadId: string; activeSession: ChatSession }> {
  let threadId = activeSession.threadId;
  if (threadId) {
    return { threadId, activeSession };
  }
  const sessionMetadata = (activeSession.metadata as Record<string, unknown> | undefined) || {};
  const mergedMetadata = {
    ...(config.agent.metadata || {}),
    ...sessionMetadata,
  };
  const threadData = await threadsClient.createThread({
    packageName: config.portal.packageName,
    systemName: config.portal.systemName,
    title: 'KorvoChat Session',
    workspaceId: typeof sessionMetadata.workspaceId === 'string' ? sessionMetadata.workspaceId : undefined,
    config: {
      provider: 'openrouter',
      model: DEFAULT_PORTAL_MODEL,
      systemPrompt: config.agent.systemPrompt,
      metadata: mergedMetadata,
      temperature: 0.7,
    },
  });
  const threadResponse = threadData as { thread?: { id?: string }; id?: string; threadId?: string };
  threadId =
    (threadResponse && (threadResponse.thread?.id || threadResponse.id || threadResponse.threadId)) || null;
  const savedSession: ChatSession = {
    ...activeSession,
    id: threadId || activeSession.id,
    threadId,
  };
  persistSession(savedSession);
  console.log('Created new thread:', threadId);
  if (!threadId) {
    throw new KorvoError(ERR_BAD_REQUEST_400, 3, { message: 'Thread creation returned no id', status: 400 });
  }
  return { threadId, activeSession: savedSession };
}
